// Define a convenience method and use it like
// ready(() => {...});
var ready = (callback) => {
  if (document.readyState != "loading") callback();
  else document.addEventListener("DOMContentLoaded", callback);
};

/*
 * HISTORY STATE
 * Check if window.history is supported
 */
function historyState() {
  return window.history && window.history.replaceState;
}

/*
 * GET URL PARAMETER
 * Checks for url parameter called `name`.
 */
function getParam(name) {
  if ("URLSearchParams" in window) {
    var params = new URLSearchParams(window.location.search);
    return params.get(name);
  } else {
    // Polyfill for IE11
    var params = new RegExp("[\?&]" + name + "=([^&#]*)").exec(window.location.href);
    if (params == null) {
      return null;
    } else {
      return decodeURI(params[1]) || 0;
    }
  }
}

ready(function () {
  /* First in the list on purpose: the rest of these reach into markup
     that a given template may not carry, and a throw here would take
     every call after it with it. Dismissing the cart is not something to
     lose to an unrelated section being absent. */
  handleCloseCartDrawer();
  handleNumberStepper();
  handleVariantSelector();
  handleCloseDropdownMenus();
  handleNavToggles();
  handleMegaMenu();
  handleMegaTabs();
  handleAjaxAddToCart();
  handleStickyHeaderCompress();
  handleDrawerOffset();
  handleSearchToggle();
});

/*
 * MEGA MENU
 * Desktop top-nav dropdowns. Panels open on hover via CSS; this adds
 * click/keyboard support (for touch and keyboard users), closes other
 * open panels, and closes on outside click or Escape.
 */
function handleMegaMenu() {
  var items = document.querySelectorAll('[data-mega-item]');
  if (!items.length) return;

  function closeItem(item) {
    item.classList.remove('is-open');
    var trigger = item.querySelector('[data-mega-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function closeAll(except) {
    items.forEach(function (item) {
      if (item !== except) closeItem(item);
    });
  }

  items.forEach(function (item) {
    var trigger = item.querySelector('[data-mega-trigger]');
    if (!trigger) return;

    trigger.addEventListener('click', function (event) {
      event.stopPropagation();
      var isOpen = item.classList.contains('is-open');
      closeAll(item);
      item.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });
  });

  document.addEventListener('click', function (event) {
    items.forEach(function (item) {
      if (item.classList.contains('is-open') && !item.contains(event.target)) {
        closeItem(item);
      }
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAll();
  });
}

/*
 * MEGA MENU TABS
 * The tabbed panel layout (opt-in per menu item). Follows the WAI-ARIA
 * vertical tablist pattern: one tab in the tab order at a time, arrows and
 * Home/End move the selection, and the chosen tab is the only one whose
 * panel is in the document's flow.
 */
function handleMegaTabs() {
  var groups = document.querySelectorAll('[data-mega-tabs]');
  if (!groups.length) return;

  groups.forEach(function (group) {
    var tabs = Array.prototype.slice.call(group.querySelectorAll('[data-mega-tab]'));
    var panels = Array.prototype.slice.call(group.querySelectorAll('[data-mega-tabpanel]'));
    if (tabs.length < 2) return;

    function select(index, moveFocus) {
      tabs.forEach(function (tab, i) {
        var on = i === index;
        tab.setAttribute('aria-selected', String(on));
        tab.setAttribute('tabindex', on ? '0' : '-1');
        if (panels[i]) panels[i].hidden = !on;
      });
      if (moveFocus) tabs[index].focus();
    }

    // Pointing at a tab is enough to read it on a desktop; the click is only
    // there for anyone who cannot hover. Asked for on a coarse pointer this
    // would fire on the tap that is already a click, so it is asked of the
    // pointer rather than of the screen's width.
    var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var hoverTimer = null;

    function cancelHover() {
      if (hoverTimer) {
        clearTimeout(hoverTimer);
        hoverTimer = null;
      }
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function (event) {
        // The panel closes on a click outside it, and a tab is inside.
        event.stopPropagation();
        cancelHover();
        select(i, false);
      });

      if (canHover) {
        tab.addEventListener('mouseenter', function () {
          // A short wait before switching: the pointer on its way from a tab
          // out to the links it opened clips the tabs under it, and without
          // this the list would change out from under the hand that is
          // reaching for it. Focus stays where it is -- the selection follows
          // the pointer, but nothing is stolen from the keyboard.
          cancelHover();
          hoverTimer = setTimeout(function () {
            hoverTimer = null;
            select(i, false);
          }, 90);
        });

        tab.addEventListener('mouseleave', cancelHover);
      }

      tab.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          next = (i + 1) % tabs.length;
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          next = (i - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = tabs.length - 1;
        }
        if (next === null) return;
        event.preventDefault();
        select(next, true);
      });
    });
  });
}

/*
 * SEARCH TOGGLE
 * Opens the full-width search panel under the header, closes on outside
 * click, Escape, or the toggle itself when the field is empty. The state
 * lives on the header rather than on the icon: the panel hangs off the bar,
 * so both the icon's active tint and the panel's reveal key to the same
 * class.
 */
function handleSearchToggle() {
  var header = document.querySelector('header.peel-header');
  if (!header) return;
  var toggle = header.querySelector('[data-peel-search-toggle]');
  var panel = header.querySelector('[data-peel-search-panel]');
  if (!toggle || !panel) return;
  var input = panel.querySelector('input[type="search"]');
  if (!input) return;

  function isOpen() {
    return header.classList.contains('is-search-open');
  }

  /*
   * The chips are shortcuts for a shopper who has not said what they want
   * yet, so they give way to the predictive results the moment one does.
   */
  function syncSuggestions() {
    header.classList.toggle('is-searching', input.value.trim().length > 0);
  }

  function open() {
    header.classList.add('is-search-open');
    toggle.setAttribute('aria-expanded', 'true');
    syncSuggestions();
    /*
     * The panel is visibility: hidden until the class lands, and a hidden
     * field cannot take focus. A timer can beat the style recalc to it and
     * focus nothing at all; waiting out a frame means the panel is really
     * visible by the time the caret is asked for.
     */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        input.focus();
      });
    });
  }

  function close() {
    header.classList.remove('is-search-open');
    toggle.setAttribute('aria-expanded', 'false');
  }

  toggle.addEventListener('click', function (event) {
    event.stopPropagation();
    if (!isOpen()) {
      open();
    } else if (!input.value) {
      close();
    } else {
      input.focus();
    }
  });

  input.addEventListener('input', syncSuggestions);

  document.addEventListener('click', function (event) {
    if (!isOpen()) return;
    if (toggle.contains(event.target) || panel.contains(event.target)) return;
    close();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isOpen()) {
      close();
      toggle.focus();
    }
  });
}

/*
 * STICKY HEADER COMPRESS
 * Tightens the sticky header's padding once the page has scrolled a bit.
 */
function handleStickyHeaderCompress() {
  var header = document.querySelector('header.peel-header');
  if (!header) return;
  var ticking = false;
  function apply() {
    ticking = false;
    header.classList.toggle('is-compressed', window.scrollY > 120);
  }
  window.addEventListener(
    'scroll',
    function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    },
    { passive: true }
  );
  apply();
}

/*
 * DRAWER OFFSET
 * The nav drawer opens below the header rather than behind it, so it needs
 * to know where the bar ends. That edge moves — the header compresses on
 * scroll and an announcement bar above it scrolls away — so it is measured
 * and published as --drawer-top for theme.css to read.
 */
function updateDrawerOffset() {
  var header = document.querySelector('header.peel-header');
  if (!header) return;
  var bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  document.documentElement.style.setProperty('--drawer-top', bottom + 'px');
}

function handleDrawerOffset() {
  var header = document.querySelector('header.peel-header');
  if (!header) return;
  var ticking = false;
  function schedule() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      updateDrawerOffset();
    });
  }
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  // The compressed bar animates its padding, so the height at the end of
  // that transition is the one the drawer has to line up with.
  header.addEventListener('transitionend', schedule);
  updateDrawerOffset();
}

/*
 * FORMAT MONEY
 * Formats the passed value as money with currency symbol.
 * Referenced from https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
 */
var Shopify = Shopify || {};
Shopify.money_format = "${{amount}}";

/*
 * CATALOG PRICING
 * Mirror of snippets/peel-catalog-cents.liquid for the prices this file and
 * peel-pdp.js write after the page has rendered — a variant swap has to land
 * on the same figure Liquid printed. window.PeelCatalogPricing is set in
 * layout/theme.liquid and holds the percentage of the list price the shopper
 * in front of us pays: 100 for everybody, less for a tagged catalog customer.
 * Display only, exactly as in Liquid: the cart still totals list prices.
 */
Shopify.catalogPrice = function (cents) {
  var rate =
    window.PeelCatalogPricing && window.PeelCatalogPricing.rate
      ? window.PeelCatalogPricing.rate
      : 100;
  if (rate === 100) {
    return cents;
  }
  // Rounded to the nearest cent the same way Liquid rounds it.
  return Math.floor((cents * rate + 50) / 100);
};
Shopify.formatMoney = function (cents, format) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || this.money_format;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

/*
 * OPTION SELECTOR
 * Works the variant option dropdowns used on the
 * product page template.
 */
function handleVariantSelector() {
  var variant;
  var options = [];
  options[1] = null;
  options[2] = null;
  options[3] = null;

  var variantOptions = document.querySelectorAll(".js--variant-option");
  variantOptions.forEach(function (el) {
    el.addEventListener("change", function (event) {
      // Disable non-existent variant options.
      checkVariants();

      // Set the chosen options. Radios only count when checked; a <select>
      // always has a current value, so it always counts.
      variantOptions.forEach(function (opt) {
        var tag = opt.tagName.toLowerCase();
        if ((tag == "input" && opt.checked == true) || tag == "select") {
          var optionName = opt.getAttribute("name");
          var optionPos = parseInt(optionName.replace("option", ""));
          var optionValue = opt.value;
          options[optionPos] = optionValue;
        }
      });

      // Loop through the variants and get the selected one.
      variants.filter(function (v) {
        if (v.option1 == options[1] && v.option2 == options[2] && v.option3 == options[3]) {
          variant = v;

          // Update variant id and prices
          document.querySelector("input#js--variant-id").value = v.id;
          // The following is used for the "purchase together" feature.
          document.querySelectorAll('input[type="checkbox"].js--variant-id').forEach(function (el) {
            el.value = v.id;
            el.setAttribute("data-price", v.price);
            if (v.available == true) {
              el.checked = true;
              el.disabled = false;
            } else {
              el.checked = false;
              el.disabled = true;
            }
          });
          var catalogPrice = Shopify.catalogPrice(v.price);
          document.querySelectorAll(".js--variant-price").forEach(function (el) {
            el.innerHTML = Shopify.formatMoney(catalogPrice);
          });

          if (v.compare_at_price > catalogPrice) {
            document.querySelectorAll(".js--variant-compareatprice").forEach(function (el) {
              el.innerText = Shopify.formatMoney(v.compare_at_price);
            });
          } else {
            document.querySelectorAll(".js--variant-compareatprice").forEach(function (el) {
              el.innerText = "";
            });
          }

          // Update SKU
          document.querySelectorAll(".js--variant-sku").forEach(function (el) {
            el.innerText = variant.sku;
          });

          // Update the current option-value label shown beside a swatch group
          [1, 2, 3].forEach(function (pos) {
            var optionValue = v["option" + pos];
            if (optionValue == null) return;
            document.querySelectorAll(".js--variant-optionname-option" + pos).forEach(function (el) {
              el.innerText = optionValue;
            });
          });

          // Disable the buy button if product is unavailable
          if (v.available === false) {
            document.querySelector('[data-js="addtocart"]').disabled = true;
            document.querySelector('[data-js="addtocart"]').innerText = "Unavailable";
          } else {
            document.querySelector('[data-js="addtocart"]').disabled = false;
            document.querySelector('[data-js="addtocart"]').innerText = "Add to cart";
          }

          // Update image
          var imageId = v.featured_image != null ? v.featured_image.id : default_image;
          var imageIndex = document
            .querySelector('.product-image-item[data-imageid="' + imageId + '"]')
            .getAttribute("data-index");
          var sliderContainer = document.querySelector(".product-images-component");
          goToImage(imageIndex, sliderContainer);

          // Append the variant ID as a url parameter
          if (v != undefined) {
            if (historyState()) {
              window.history.replaceState({}, document.title, "?variant=" + v.id);
            }
          }
        }
      });
    });
  });
}

/*
 * CHECK VARIANT EXISTS
 * This checks if the variant actually exists - e.g you may have small, medium,
 * and large in blue and black, but blue/medium is not a variant. Disables the
 * related inputs.
 */
function checkVariants() {
  let $this = event.target;
  if ($this !== undefined) {
    let availableVariants = new Set();
    variants.filter(function (variant, k) {
      if (variant[$this.name] == $this.value) {
        availableVariants.add(variant);
      }
    });

    // Loop through the array above to create an array containing available
    // option values.
    let optionGroups = {};
    availableVariants.forEach(function (variant) {
      let options = Object.entries(variant);
      for (const [key, value] of options) {
        if (value != null) {
          if (optionGroups[key] == undefined) {
            optionGroups[key] = [];
          }
          if (optionGroups[key].includes(value) == false) {
            optionGroups[key].push(value);
          }
        }
      }
    });

    // Then, loop through each input and check if its value is in the optionGroups
    // array created above, ignoring the clicked option group. For a <select>,
    // disable individual <option>s instead of the whole element.
    document.querySelectorAll(".js--variant-option").forEach(function (input) {
      if (input.name != $this.name) {
        if (input.tagName.toLowerCase() == "select") {
          input.querySelectorAll("option").forEach(function (opt) {
            opt.disabled = optionGroups[input.name].includes(opt.value) == false;
          });
        } else if (optionGroups[input.name].includes(input.value) == false) {
          input.disabled = true;
          input.checked = false;
        } else {
          input.disabled = false;
        }
      }
    });
  }

  // Check a valid option is selected for each group. Selects always have a
  // current value, so this only applies to radio groups.
  document.querySelectorAll(".js--variant-options").forEach(function (group) {
    if (group.querySelector("select")) return;
    let firstAvailable = null;
    let checkedOptions = group.querySelectorAll(".js--variant-option:checked").length;
    if (checkedOptions == 0) {
      firstAvailable = group.querySelectorAll(".js--variant-option:not(:disabled)")[0];
    }
    if (firstAvailable != null) {
      firstAvailable.checked = true;
    }
  });
}

/*
 * TOGGLE MOBILE NAV
 * Open/close the nav drawer phones and iPads get in place of the top nav.
 * This just applies a class and the rest is done in CSS.
 */
function toggleNav() {
  let nav = document.querySelector("#header-nav");
  // Measure the header now: the drawer has to open flush under whatever
  // height the bar happens to be at this scroll position.
  updateDrawerOffset();
  let open = nav.classList.toggle("nav-open");
  // The hamburger is the only thing on screen saying whether the flyout
  // is open, so keep its state readable to a screen reader too.
  document.querySelectorAll('[aria-controls="header-nav"]').forEach(function (btn) {
    btn.setAttribute("aria-expanded", open ? "true" : "false");
    btn.setAttribute("aria-label", open ? "Close menu" : "Open menu");
  });
}

/*
 * NUMBER INPUT STEPPER
 * Number stepper used for the quantity field.
 */
function handleNumberStepper() {
  let maxQty = 20;
  let minQty = 1;
  const steppers = document.querySelectorAll(".input-stepper");

  steppers.forEach(function (stepper) {
    let input = stepper.querySelector('input[type="number"]');
    let stepperButtons = stepper.querySelectorAll("button");
    let maxValue = input.getAttribute("max") !== undefined ? parseInt(input.getAttribute("max")) : maxQty;
    let minValue = input.getAttribute("min") !== undefined ? parseInt(input.getAttribute("min")) : minQty;

    stepperButtons.forEach(function (button) {
      button.addEventListener("click", function (event) {
        let currentValue = parseInt(input.value);
        if (button.classList.contains("input-stepper_decrease")) {
          if (currentValue > minValue) {
            input.value = currentValue - 1;
          }
        }
        if (button.classList.contains("input-stepper_increase")) {
          if (currentValue < maxValue) {
            input.value = currentValue + 1;
          }
        }
      });
    });
  });
}

/*
 * CLOSE NAV DROPDOWNS
 * Closes nav menus on escape key press. This is primarly
 * for keyboard users/accessibility.
 */
function handleCloseDropdownMenus() {
  const nav = document.querySelector("#header-nav");
  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape") {
      nav.querySelectorAll("details[open]").forEach((detail) => {
        detail.removeAttribute("open");
      });
    }
  });
}

/*
 * NAV TOGGLES
 * Handles the nav toggles, so only one menu is open at a  time.
 */
function handleNavToggles() {
  const nav = document.getElementById("header-nav");
  let details = nav.querySelectorAll("details");
  details.forEach(function (detail) {
    detail.addEventListener("toggle", function (event) {
      if (detail.open) {
        details.forEach((otherDetail) => {
          if (
            otherDetail !== detail &&
            otherDetail.open &&
            !otherDetail.contains(detail) &&
            !detail.contains(otherDetail)
          ) {
            otherDetail.open = false;
          }
        });
      }
    });
  });
}

/*
 * ADD ITEM TO CART.
 */
function handleAjaxAddToCart() {
  const btns = document.querySelectorAll('[data-js="addtocart"]');
  btns.forEach((btn) => {
    // Collection grids re-render their cards (filters, the device fit
    // filter), so this runs again on the new buttons — skip any that are
    // already listening rather than adding a second listener.
    if (btn.dataset.cartBound) return;
    btn.dataset.cartBound = "true";
    let parentForm = btn.closest("form");
    btn.addEventListener("click", function (event) {
      event.preventDefault();

      // Replace button text with waiting icon and disable it.
      let btnLabel = btn.innerHTML.trim();
      btn.innerHTML = `
        <svg style="margin:0 auto;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" style="shape-rendering: auto; display: block; background: none;" width="16" height="16" xmlns:xlink="http://www.w3.org/1999/xlink"><g><circle stroke-dasharray="188.49555921538757 64.83185307179586" r="40" stroke-width="15" stroke="#ffffff" fill="none" cy="50" cx="50">
          <animateTransform keyTimes="0;1" values="0 50 50;360 50 50" dur="1s" repeatCount="indefinite" type="rotate" attributeName="transform"></animateTransform>
        </circle><g></g></g></svg>`;
      btn.disabled = true;

      // Get the variant details and add to cart.
      let variantId = parentForm.querySelector('input[name="id"]').value;
      let quantity = parentForm.querySelector('input[name="quantity"]').value;
      let items = [
        {
          id: variantId,
          quantity: parseInt(quantity),
        },
      ];

      // Include any checked "Complete the setup" addon items alongside
      // the main product.
      document.querySelectorAll(".js--purchase-item:checked:not(:disabled)").forEach(function (checkbox) {
        items.push({
          id: checkbox.value,
          quantity: 1,
        });
      });

      let productData = {
        items: items,
        sections: ["cart-drawer"],
      };
      fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(productData),
      })
        .then((response) => response.json())
        .then((cart) => {
          if (document.getElementById("shopify-section-cart-drawer")) {
            document.getElementById("shopify-section-cart-drawer").outerHTML = cart.sections["cart-drawer"];
          }
          cartDrawer.showModal();
          btn.innerHTML = btnLabel;
          btn.disabled = false;
          fetch(window.Shopify.routes.root + "cart.js")
            .then((response) => response.json())
            .then((cart) => {
              _updateCartItemCount(cart.item_count);
            });
        });
    });
  });
}

/*
 * ADD-ON DISCOUNT
 * The product page prices its add-on cards from its own settings, which
 * only changes what the shopper reads. This is the half that changes what
 * they pay: the code set in Theme settings > Add-on discount, put on the
 * cart the moment an add-on goes into it.
 *
 * The code is expected to be restricted to the add-on products in the
 * admin, so a cart that no longer holds one is already worth nothing to
 * it. syncAddonDiscount below is tidiness on top of that guarantee, not
 * the thing providing it.
 */
/* Every code the add-on offers may have applied, lowercased for comparison
   and with the blanks — unset rules — dropped. */
function addonDiscountCodes() {
  const configured = window.peelAddonDiscountCodes || [];
  return configured.map((code) => String(code).trim()).filter(Boolean);
}

/* One attempt at a discount list. Answers with the re-rendered cart, or
   null when Shopify would not take it — and says so on the console rather
   than only in the return value, because a rejected code and a code that
   was never sent look identical on the page. */
function postAddonDiscount(discount, sections) {
  return fetch(window.Shopify.routes.root + "cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ discount: discount, sections: sections || [] }),
  })
    .then((response) => {
      if (response.ok) return response.json();
      if (window.console) {
        console.warn("Add-on discount not applied: " + discount + " (HTTP " + response.status + ")");
      }
      return null;
    })
    .catch(() => null);
}

/* /cart/update.js takes the whole discount list, comma separated, so two
   add-ons discounted by two different rules can apply both of their codes
   — but only where those codes are set to combine in the admin. Where they
   are not, Shopify turns down the set, which would leave a cart with no
   discount at all rather than the one it could have had. So a rejected set
   is retried with the first code alone: one real discount beats none. */
function applyAddonDiscount(codes, sections) {
  const wanted = (codes || []).map((code) => String(code).trim()).filter(Boolean);
  if (!wanted.length) return Promise.resolve(null);

  return postAddonDiscount(wanted.join(","), sections).then((cart) => {
    if (cart || wanted.length === 1) return cart;
    return postAddonDiscount(wanted[0], sections);
  });
}

/*
 * Adds one code to the ones the cart already holds, for an add-on bought
 * from inside the cart rather than alongside a product. By then the cart
 * can be carrying codes of its own — a bundle builder's, one from a
 * /discount/ link — and /cart/update.js replaces the whole list, so ours
 * posted alone would take theirs off. The cart is read first for that
 * reason; when it can't be read nothing is sent, on the same rule as
 * syncAddonDiscount. The codes already there go first, so a set Shopify
 * won't combine falls back to what the customer had rather than to ours.
 *
 * Answers with the re-rendered cart when it applied the code, and null
 * when there was nothing to do.
 */
function addAddonDiscount(code, sections) {
  const wanted = String(code || "").trim();
  if (!wanted) return Promise.resolve(null);

  return fetch(window.Shopify.routes.root + "cart.js")
    .then((response) => (response.ok ? response.json() : null))
    .then((cart) => {
      if (!cart || !Array.isArray(cart.discount_codes)) return null;
      const held = cart.discount_codes.map((entry) => String(entry.code || "").trim()).filter(Boolean);
      if (held.some((entry) => entry.toLowerCase() === wanted.toLowerCase())) return null;
      return applyAddonDiscount(held.concat(wanted), sections);
    })
    .catch(() => null);
}

/*
 * Takes the code back off once the last add-on has left the cart — but
 * only where it is provably the one code on there. /cart/update.js sets the
 * whole discount list rather than one entry of it, so clearing a cart whose
 * codes cannot be read would take a code the customer typed with it. When
 * discount_codes is missing from the response, or holds anything besides
 * ours, this does nothing and the restricted code is left to be worth
 * nothing on its own.
 *
 * Answers with the re-rendered cart when it cleared one, and null when
 * there was nothing to do, so a caller can render whichever is newer.
 */
function syncAddonDiscount(cart, sections) {
  const ours = addonDiscountCodes().map((code) => code.toLowerCase());
  if (!ours.length || !cart || !Array.isArray(cart.items)) return Promise.resolve(null);

  const applied = cart.discount_codes;
  if (!Array.isArray(applied) || !applied.length) return Promise.resolve(null);
  /* Every code on the cart has to be one of ours. One the customer typed
     among them and the list goes back untouched, because clearing sets the
     whole list rather than one entry of it. */
  const allOurs = applied.every((entry) => ours.indexOf(String(entry.code || "").toLowerCase()) !== -1);
  if (!allOurs) return Promise.resolve(null);

  const keptAnAddon = cart.items.some((item) => item.properties && item.properties._addon_source);
  if (keptAnAddon) return Promise.resolve(null);

  return fetch(window.Shopify.routes.root + "cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ discount: "", sections: sections || [] }),
  })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);
}

/*
 * UPDATE CART LINE ITEM
 * Updates/removes the line item from cart drawer.
 *
 * data-keys carries several line keys instead of data-key's one, which is
 * how a bundle's controls move all of its pieces at once: the pieces are
 * separate SKUs on separate lines, but a stepper or a remove on the bundle
 * card has to reach every one of them or the customer is left holding a
 * case with no charger under a heading that still says bundle.
 */
function updateCartLineItem(el) {
  let keys = el.dataset.keys ? el.dataset.keys.split(",") : [el.dataset.key];
  let qty = el.value;
  if (qty !== "" && qty >= 0) {
    let cartItemsSection = document.querySelector("#cartDrawer-items");
    document.getElementById("cartDrawer-content").classList.add("waiting");

    let updates = {};
    keys.forEach((key) => {
      updates[key] = qty;
    });

    let data = {
      updates: updates,
      sections: ["cart-drawer"],
    };
    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      /* Removing a line can be the removal of the last add-on, which is
         what the discount was for. Resolved before the render so the
         drawer is drawn once, from whichever cart is newer. */
      .then((cart) => syncAddonDiscount(cart, ["cart-drawer"]).then((cleared) => cleared || cart))
      .then((cart) => {
        if (document.getElementById("shopify-section-cart-drawer")) {
          document.getElementById("shopify-section-cart-drawer").outerHTML = cart.sections["cart-drawer"];
        }
        _updateCartItemCount(cart.item_count);
        document.getElementById("cartDrawer-content").classList.remove("waiting");
        cartDrawer.showModal();
      });
  }
}

/*
 * CLEAR CART
 * Empties the cart from the drawer. The first click arms the button and
 * swaps its label; a second click within four seconds does the clearing.
 * There is no undo, so the confirmation step is the whole point.
 */
function clearCart(el) {
  if (el.dataset.armed !== "true") {
    el.dataset.armed = "true";
    el.innerText = el.dataset.confirmLabel;
    el.classList.add("is-confirming");
    window.clearTimeout(el._disarmTimer);
    el._disarmTimer = window.setTimeout(() => {
      el.dataset.armed = "false";
      el.innerText = el.dataset.label;
      el.classList.remove("is-confirming");
    }, 4000);
    return;
  }

  window.clearTimeout(el._disarmTimer);
  el.disabled = true;
  document.getElementById("cartDrawer-content").classList.add("waiting");

  fetch(window.Shopify.routes.root + "cart/clear.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ sections: ["cart-drawer"] }),
  })
    .then((response) => response.json())
    .then((cart) => {
      if (document.getElementById("shopify-section-cart-drawer")) {
        document.getElementById("shopify-section-cart-drawer").outerHTML = cart.sections["cart-drawer"];
      }
      _updateCartItemCount(cart.item_count);
      cartDrawer.showModal();
    })
    .catch(() => {
      let content = document.getElementById("cartDrawer-content");
      if (content) content.classList.remove("waiting");
      el.disabled = false;
      el.dataset.armed = "false";
      el.innerText = el.dataset.label;
      el.classList.remove("is-confirming");
    });
}

/*
 * ADD COMPLEMENTARY PRODUCT TO CART
 * Adds a cart-drawer addon suggestion (a complementary product) as a new
 * line item. Uses inline onclick (like updateCartLineItem) rather than an
 * attached listener, since this button lives inside the cart-drawer
 * section and is re-rendered via outerHTML on every cart change.
 *
 * A suggestion priced as a bundle — a glass beside a case already in the
 * cart — carries the code that pays for it in data-addon-code. Its line
 * is stamped the way the product page stamps an add-on, so that
 * syncAddonDiscount keeps the code while the glass is in the cart and
 * takes it back off once it isn't.
 */
function addComplementaryToCart(el) {
  if (el.disabled) return;
  let variantId = el.dataset.variantId;
  if (!variantId) return;
  let addonCode = (el.dataset.addonCode || "").trim();
  el.disabled = true;
  document.getElementById("cartDrawer-content").classList.add("waiting");

  let item = { id: variantId, quantity: 1 };
  if (addonCode) item.properties = { _addon_source: "cart" };

  let data = {
    items: [item],
    sections: ["cart-drawer"],
  };
  fetch(window.Shopify.routes.root + "cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then((response) => response.json())
    /* Resolved before the render, as on the product page, so the drawer
       opens once, on the total the checkout will actually charge. */
    .then((cart) => {
      /* A refused add — sold out since the drawer was drawn — answers
         with an error and no items, and a code for a glass that never
         went in is not one to put on the cart. */
      if (!addonCode || !cart || !Array.isArray(cart.items)) return cart;
      return addAddonDiscount(addonCode, ["cart-drawer"]).then((discounted) => discounted || cart);
    })
    .then((cart) => {
      if (document.getElementById("shopify-section-cart-drawer")) {
        document.getElementById("shopify-section-cart-drawer").outerHTML = cart.sections["cart-drawer"];
      }
      _updateCartItemCount(cart.item_count);
      cartDrawer.showModal();
    })
    .catch(() => {
      let content = document.getElementById("cartDrawer-content");
      if (content) content.classList.remove("waiting");
      el.disabled = false;
    });
}

/*
 * UPDATE CART ITEM COUNT
 * Callers holding a cart object pass its item_count. cart/add.js is the
 * exception: it answers with the lines it just added rather than with the
 * cart, so its item_count is undefined — and writing that straight into the
 * header is how "Cart · undefined" happened. Those callers fall through to
 * the cart-drawer section that the same response re-rendered, which carries
 * the real count. With neither, the header keeps the number it has: a count
 * one add behind still describes a cart, "undefined" does not.
 */
function _cartDrawerItemCount() {
  let drawer = document.getElementById("cartDrawer-content");
  if (!drawer) return null;
  let count = Number(drawer.dataset.cartCount);
  return Number.isFinite(count) ? count : null;
}

function _updateCartItemCount(count) {
  let resolved = typeof count === "number" && Number.isFinite(count) ? count : _cartDrawerItemCount();
  if (resolved === null) return;

  let instances = document.querySelectorAll('[data-js="cart-counter"]');
  instances.forEach((el) => {
    el.innerText = resolved;
  });
}

/*
 * CLOSE CART DRAWER FROM OUTSIDE IT
 * Escape, and a click anywhere on the dimmed page beside the panel — the
 * same dismissal the collection filter panel's scrim gives.
 *
 * The drawer is a native <dialog> opened with showModal(), so what is
 * dimmed beside it is its ::backdrop: a pseudo-element with no node of
 * its own, which means a press there arrives with the dialog itself as
 * the event target. The panel's own chrome all lives inside
 * #cartDrawer-content, so requiring the target to be the dialog is what
 * separates a press on the backdrop from a press on the cart.
 *
 * The press is checked against the dialog's rect as well, and remembered
 * across to the click, for the two cases the target alone gets wrong: a
 * button inside the panel activated from the keyboard reports a click at
 * (0, 0) with no press before it, and a drag that starts in the panel and
 * ends on the backdrop — selecting a line, running a quantity stepper
 * past the edge — is dispatched on the two ends' common ancestor, which
 * is the dialog. Neither should dismiss the cart.
 *
 * All of it is delegated from the document: every cart update replaces
 * #shopify-section-cart-drawer wholesale, dialog and all, so a listener
 * bound to the element would not survive the first add to cart.
 */
function handleCloseCartDrawer() {
  let pressedOutside = false;

  const openDrawer = () => {
    const drawer = document.getElementById("cartDrawer");
    return drawer && drawer.open ? drawer : null;
  };

  const isOutside = (drawer, event) => {
    const rect = drawer.getBoundingClientRect();
    return (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    );
  };

  document.addEventListener("pointerdown", (event) => {
    const drawer = openDrawer();
    pressedOutside = !!drawer && event.target === drawer && isOutside(drawer, event);
  });

  document.addEventListener("click", (event) => {
    const drawer = openDrawer();
    if (!drawer || !pressedOutside) return;
    pressedOutside = false;
    if (event.target !== drawer || !isOutside(drawer, event)) return;
    drawer.close();
  });

  document.addEventListener("keydown", (event) => {
    const drawer = openDrawer();
    if (event.key === "Escape" && drawer) {
      drawer.close();
    }
  });
}
